import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Check, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

/**
 * Language Switcher Component
 * Can be used on auth pages and anywhere else language selection is needed
 * 
 * @param {string} variant - 'default' | 'minimal' | 'auth' - styling variant
 * @param {string} className - additional classes
 */
export default function LanguageSwitcher({ variant = 'default', className }) {
  const { language, setLanguage, languages } = useLanguage();
  
  const currentLang = languages.find(l => l.code === language) || languages[0];

  const variantStyles = {
    default: 'text-wp-light/70 hover:text-wp-lightest hover:bg-wp-dark/30',
    minimal: 'text-wp-light/50 hover:text-wp-lightest hover:bg-wp-dark/20',
    auth: 'text-white/60 hover:text-white hover:bg-white/10 backdrop-blur-sm',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-2 rounded-xl transition-all duration-200",
            variantStyles[variant],
            className
          )}
          data-testid="language-switcher"
        >
          <span className="text-lg">{currentLang.flag}</span>
          <span className="font-medium text-sm">{currentLang.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-wp-darker border-wp-dark/50"
      >
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={cn(
              "flex items-center gap-3 cursor-pointer text-wp-light/70 hover:text-wp-lightest focus:text-wp-lightest hover:bg-wp-dark/30 focus:bg-wp-dark/30",
              language === lang.code && "text-wp-lightest bg-wp-medium/20"
            )}
            data-testid={`lang-option-${lang.code}`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="font-medium">{lang.name}</span>
            {language === lang.code && (
              <Check className="h-4 w-4 ml-auto text-wp-lightest" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
